/**
 * Full-screen image viewer for a gallery — opened by clicking a thumbnail
 * instead of navigating to the raw file in a new tab. Same accessible-modal
 * pattern as `ConfirmDialog` (focus moved in on open, restored on close,
 * Escape to close), plus Left/Right arrow-key and on-screen prev/next
 * navigation across the gallery's images.
 */
import { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { PropertyPicture } from './PropertyPicture';

export interface LightboxImage {
  src: string;
  alt: string;
}

export interface LightboxProps {
  images: LightboxImage[];
  /** Index into `images` of the open image, or `null` when closed. */
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export function Lightbox({ images, index, onClose, onNavigate }: LightboxProps): React.JSX.Element | null {
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = index !== null;

  useEffect(() => {
    if (!open || index === null) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent): void {
      if (index === null) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      } else if (event.key === 'ArrowRight' && images.length > 1) {
        event.preventDefault();
        onNavigate((index + 1) % images.length);
      } else if (event.key === 'ArrowLeft' && images.length > 1) {
        event.preventDefault();
        onNavigate((index - 1 + images.length) % images.length);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [open, index, images.length, onClose, onNavigate]);

  if (index === null) return null;
  const current = images[index];
  if (current === undefined) return null;

  return (
    <div className="lightbox__backdrop" onClick={onClose}>
      <div
        className="lightbox"
        role="dialog"
        aria-modal="true"
        aria-label={current.alt}
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="lightbox__close" ref={closeRef} onClick={onClose} aria-label="Close">
          <X size={22} aria-hidden="true" />
        </button>

        {images.length > 1 && (
          <button
            type="button"
            className="lightbox__nav lightbox__nav--prev"
            onClick={() => onNavigate((index - 1 + images.length) % images.length)}
            aria-label="Previous image"
          >
            <ChevronLeft size={28} aria-hidden="true" />
          </button>
        )}

        <PropertyPicture
          src={current.src}
          alt={current.alt}
          size="full"
          loading="eager"
          className="lightbox__image"
        />

        {images.length > 1 && (
          <button
            type="button"
            className="lightbox__nav lightbox__nav--next"
            onClick={() => onNavigate((index + 1) % images.length)}
            aria-label="Next image"
          >
            <ChevronRight size={28} aria-hidden="true" />
          </button>
        )}

        {images.length > 1 && (
          <p className="lightbox__counter">
            {index + 1} / {images.length}
          </p>
        )}
      </div>
    </div>
  );
}

export default Lightbox;
